import { Pipe, PipeTransform } from "@angular/core";
import { getOrdinal, toDate } from "src/app/shared/utils/date.util";

@Pipe({
	name: "suffixLocalTimezoneDate"
})
export class SuffixLocalTimezoneDatePipe implements PipeTransform {
	private readonly formatter = new Intl.DateTimeFormat("en-US", {
		timeZone: "UTC",
		month: "long",
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
		hourCycle: "h12"
	});

	transform(value: Date | string | null | undefined): string {
		if (!value) return "";

		const date: Date = this.parseAsUtc(value);
		if (Number.isNaN(date.getTime())) return "";

		const parts = this.formatter.formatToParts(this.toStandardTime(date));
		const part = (type: Intl.DateTimeFormatPartTypes): string => parts.find(p => p.type === type)?.value ?? "";

		let month = part("month");
		if (month.length > 5) {
			month = month.substring(0, 3);
		}

		const day = getOrdinal(Number(part("day")));

		return `${month} ${day} / ${this.formatTime(part("hour"), part("minute"), part("dayPeriod"))}`;
	}

	// Backend timestamps without an offset are UTC; native parsing would treat them as local time.
	private parseAsUtc(value: Date | string): Date {
		if (typeof value === "string") {
			const trimmed = value.trim();
			const timestampWithoutOffset = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/;

			if (timestampWithoutOffset.test(trimmed)) {
				return new Date(`${trimmed}Z`);
			}
		}

		return toDate(value);
	}

	// Display local time using the browser's standard offset, without applying DST twice.
	private toStandardTime(date: Date): Date {
		const year = date.getUTCFullYear();
		const standardOffset = Math.max(
			new Date(year, 0, 1).getTimezoneOffset(),
			new Date(year, 6, 1).getTimezoneOffset()
		);

		return new Date(date.getTime() - standardOffset * 60_000);
	}

	private formatTime(hour: string, minute: string, dayPeriod: string): string {
		const minutes = minute === "00" ? "" : `:${minute}`;
		return `${Number(hour)}${minutes} ${dayPeriod.toUpperCase()}`;
	}
}
